import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    Collapse,
    Paper,
    Chip,
    Tooltip,
    makeStyles
} from '@material-ui/core';
import { ChevronDownIcon } from '../../../../icons';
import React, { useState } from 'react';
import moment from 'moment';
import TablePager from '../../../../components/atom/TablePager';

const useStyles = makeStyles(theme => ({
    createAction: {
        color: '#4caf50',
        backgroundColor: '#e8f5e8'
    },
    updateAction: {
        color: '#2196f3',
        backgroundColor: '#e3f2fd'
    },
    deleteAction: {
        color: '#f44336',
        backgroundColor: '#ffebee'
    },
    loginAction: {
        color: '#ff9800',
        backgroundColor: '#fff3e0'
    },
    defaultAction: {
        color: '#9e9e9e',
        backgroundColor: '#f5f5f5'
    },
    dateCell: {
        whiteSpace: 'nowrap',
        fontSize: '0.875rem'
    },
    expandIcon: {
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest
        })
    },
    expandIconOpen: {
        transform: 'rotate(180deg)'
    }
}));

const getRecursiveIDValue = obj => {
    if (obj && typeof obj === 'object' && 'value' in obj) {
        return getRecursiveIDValue(obj.value);
    }
    if (Array.isArray(obj) || Number.isNaN(obj)) {
        return null;
    }
    return parseInt(obj, 10) || null;
};

const recursiveDateParser = obj => {
    if (obj && typeof obj === 'object' && 'value' in obj) {
        return recursiveDateParser(obj.value);
    }

    if (typeof obj !== 'string') {
        return obj;
    }

    // Strict date parsing
    const m = moment(
        obj,
        [moment.ISO_8601, 'MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD'],
        true
    );

    if (!m.isValid()) {
        return obj;
    }

    // Determine if the input explicitly contains a time component
    const hasExplicitTime = /T\d{2}:\d{2}| \d{1,2}:\d{2}/.test(obj);

    // Get the UTC time parts
    const hours = m.utc().hour();
    const minutes = m.utc().minute();
    const seconds = m.utc().second();

    // Only show time if it was explicitly provided AND not midnight
    const includeTime = hasExplicitTime && (hours !== 0 || minutes !== 0 || seconds !== 0);

    return includeTime ? m.utc().format('MM/DD/YYYY hh:mm a') : m.utc().format('MM/DD/YYYY');
};

function RenderObjectTree({ data }) {
    const isJson = value => {
        if (typeof value !== 'string' || !value.trim()) {
            return false;
        }
        const trimmed = value.trim();
        return (
            (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
            (trimmed.startsWith('[') && trimmed.endsWith(']'))
        );
    };

    const deepParse = value => {
        let current = value;
        for (let i = 0; i < 5; i++) {
            if (typeof current === 'string' && isJson(current)) {
                try {
                    const parsed = JSON.parse(current);
                    // Only continue if we actually got an object or array
                    if (typeof parsed === 'object' && parsed !== null) {
                        current = parsed;
                        continue;
                    }
                } catch {
                    // If JSON parsing fails, stop trying and return current value
                    break;
                }
            }
            break;
        }
        return current;
    };

    const draftToText = value => {
        const obj = value && typeof value === 'object' ? value : null;
        if (obj?.blocks && Array.isArray(obj.blocks)) {
            return obj.blocks
                .map(block => block.text)
                .join(' ')
                .trim();
        }
        return value;
    };

    const normalizeValue = value => draftToText(deepParse(value));

    const unwrapEnhancedValue = value => {
        // Handle enhanced field data from backend {value, description}
        if (value && typeof value === 'object' && 'value' in value) {
            return value.value;
        }
        return value;
    };

    const safeStringify = value => {
        if (value == null) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value, null, 2);
            } catch {
                return '[Complex Object]';
            }
        }
        return String(value);
    };

    // Unwrap enhanced data structure first, then try to parse JSON
    const unwrappedData = unwrapEnhancedValue(data);
    const parsedData = deepParse(unwrappedData);

    if (parsedData == null) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>null</span>;
    }

    // If it's still a string after parsing attempts, check if it looks like it should be parsed
    if (typeof parsedData === 'string') {
        // Try one more time with better detection for edge cases
        if (parsedData.trim().startsWith('[') || parsedData.trim().startsWith('{')) {
            try {
                const finalParsed = JSON.parse(parsedData.trim());
                if (typeof finalParsed === 'object' && finalParsed !== null) {
                    // Successfully parsed to object/array, render as such
                    return <RenderObjectTree data={finalParsed} />;
                }
            } catch {
                // Failed to parse, continue as string
            }
        }
        return <span>{safeStringify(normalizeValue(parsedData))}</span>;
    }

    if (typeof parsedData !== 'object') {
        return <span>{safeStringify(normalizeValue(parsedData))}</span>;
    }

    if (Array.isArray(parsedData)) {
        if (parsedData.length === 0) {
            return <span style={{ color: '#999', fontStyle: 'italic' }}>[]</span>;
        }
        return (
            <ul style={{ listStyleType: 'none', paddingLeft: 10, margin: 0 }}>
                {parsedData.map((item, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                        <RenderObjectTree data={item} />
                    </li>
                ))}
            </ul>
        );
    }

    const unwrap = (key, value) => {
        if (value && typeof value === 'object' && ('value' in value || 'description' in value)) {
            return {
                label:
                    value.description ||
                    key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                val: 'value' in value ? value.value : null,
                displayValue: value?.displayValue || null
            };
        }
        return {
            label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            val: value,
            displayValue: value?.displayValue || null
        };
    };

    const entries = Object.entries(parsedData);

    if (entries.length === 0) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>{'{}'}</span>;
    }

    return (
        <TableContainer component={Paper} style={{ marginTop: 8, marginBottom: 8 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                            Field
                        </TableCell>
                        <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                            Value
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries.map(([key, value]) => {
                        const { label, val, displayValue } = unwrap(key, value);

                        const ID = getRecursiveIDValue(val);
                        const dateValue = recursiveDateParser(val);

                        if (!value) {
                            return null;
                        }

                        return (
                            <TableRow key={key} hover>
                                <TableCell style={{ fontWeight: 500, verticalAlign: 'top' }}>
                                    {label}
                                </TableCell>
                                <TableCell style={{ wordBreak: 'break-word', maxWidth: 300 }}>
                                    <RenderObjectTree
                                        data={
                                            displayValue
                                                ? `${displayValue} ${ID ? ` [#${ID}]` : ''}`
                                                : dateValue
                                                ? dateValue
                                                : val
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const unwrapEnhancedValue = value => {
    if (
        value !== null &&
        typeof value === 'object' &&
        Object.prototype.hasOwnProperty.call(value, 'value')
    ) {
        return unwrapEnhancedValue(value.value);
    }

    return value;
};

const isJson = value => {
    if (typeof value !== 'string' || !value.trim()) {
        return false;
    }
    const trimmed = value.trim();
    return (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
    );
};

const deepParse = value => {
    let current = value;
    for (let i = 0; i < 5; i++) {
        if (typeof current === 'string' && isJson(current)) {
            try {
                const parsed = JSON.parse(current);
                // Only continue if we actually got an object or array
                if (typeof parsed === 'object' && parsed !== null) {
                    current = parsed;
                    continue;
                }
            } catch {
                // If JSON parsing fails, stop trying and return current value
                break;
            }
        }
        break;
    }
    return current;
};

const draftToText = value => {
    const isObject = value && typeof value === 'object' ? value : null;
    if (isObject?.blocks && Array.isArray(isObject.blocks)) {
        return isObject.blocks
            .map(block => block.text)
            .join(' ')
            .trim();
    }
    return value;
};

const isEmpty = value => {
    const checkedValue = value == null ? null : value;
    if (checkedValue === '' || checkedValue === 'null') {
        return true;
    }
    if (Array.isArray(checkedValue)) {
        return checkedValue.length === 0;
    }
    if (checkedValue && typeof checkedValue === 'object') {
        return Object.keys(checkedValue).length === 0;
    }
    return checkedValue == null;
};

const normalize = value => {
    // First unwrap enhanced values
    let unwrapped = unwrapEnhancedValue(value);
    let parsedValue = deepParse(unwrapped);

    // Try additional parsing for strings that look like JSON but weren't caught
    if (typeof parsedValue === 'string') {
        if (parsedValue.trim().startsWith('[') || parsedValue.trim().startsWith('{')) {
            try {
                const finalParsed = JSON.parse(parsedValue.trim());
                if (typeof finalParsed === 'object' && finalParsed !== null) {
                    parsedValue = finalParsed;
                }
            } catch {
                // Keep as string if parsing fails
            }
        }
    }
    parsedValue = recursiveDateParser(parsedValue);

    // Apply draft text conversion after all JSON parsing attempts
    parsedValue = draftToText(parsedValue);
    return parsedValue;
};

const show = value => {
    // Check for backend-provided displayValue before unwrapping
    if (value && typeof value === 'object' && value.displayValue) {
        return value.displayValue;
    }

    let displayValue = normalize(value);

    // Try to parse JSON strings that might be arrays or objects
    if (
        typeof displayValue === 'string' &&
        (displayValue.trim().startsWith('[') || displayValue.trim().startsWith('{'))
    ) {
        try {
            const parsed = JSON.parse(displayValue.trim());
            if (typeof parsed === 'object' && parsed !== null) {
                displayValue = parsed;
            }
        } catch {
            // Keep as string if parsing fails
        }
    }

    if (isEmpty(displayValue)) {
        return '<empty>';
    }

    if (Array.isArray(displayValue)) {
        if (displayValue.length === 0) {
            return '[]';
        }
        const items = displayValue.slice(0, 3).map(show); // Show first 3 items
        const result = items.join(', ');
        return displayValue.length > 3
            ? `[${result}, ...${displayValue.length - 3} more]`
            : `[${result}]`;
    }

    if (displayValue && typeof displayValue === 'object') {
        const entries = Object.entries(displayValue).slice(0, 3); // Show first 3 entries
        const entriesStr = entries.map(([k, val]) => `${k}: ${show(val)}`).join(', ');
        const totalEntries = Object.keys(displayValue).length;
        return totalEntries > 3
            ? `{${entriesStr}, ...${totalEntries - 3} more}`
            : `{${entriesStr}}`;
    }

    // Truncate long strings
    const str = String(displayValue);
    return str.length > 100 ? `${str.substring(0, 100)}...` : str;
};

const equalish = (objectA, objectB) => {
    const normalizedA = normalize(objectA);
    const normalizedB = normalize(objectB);

    // Handle null/undefined cases
    if (normalizedA === normalizedB) {
        return true;
    }
    if (normalizedA == null || normalizedB == null) {
        return false;
    }

    // For objects and arrays, use deep comparison via JSON stringify
    if (typeof normalizedA === 'object' || typeof normalizedB === 'object') {
        try {
            const stringifiedA =
                typeof normalizedA === 'object'
                    ? JSON.stringify(normalizedA, Object.keys(normalizedA).sort())
                    : String(normalizedA);
            const stringifiedB =
                typeof normalizedB === 'object'
                    ? JSON.stringify(normalizedB, Object.keys(normalizedB).sort())
                    : String(normalizedB);
            return stringifiedA === stringifiedB;
        } catch {
            // Fallback to string comparison if JSON.stringify fails
            return String(normalizedA) === String(normalizedB);
        }
    }

    // Direct comparison for primitives
    return String(normalizedA) === String(normalizedB);
};

const objectDiff = (objectA, objectB) => {
    const normalizedA = normalize(objectA);
    const normalizedB = normalize(objectB);

    // Both are objects/arrays - do deep comparison
    if (
        normalizedA &&
        typeof normalizedA === 'object' &&
        !Array.isArray(normalizedA) &&
        normalizedB &&
        typeof normalizedB === 'object' &&
        !Array.isArray(normalizedB)
    ) {
        const keys = new Set([...Object.keys(normalizedA), ...Object.keys(normalizedB)]);
        return [...keys]
            .filter(k => !equalish(normalizedA[k], normalizedB[k]))
            .map(k => [k, normalizedA[k], normalizedB[k]]);
    }

    // Arrays - treat as single values for diff purposes
    if (Array.isArray(normalizedA) || Array.isArray(normalizedB)) {
        return equalish(normalizedA, normalizedB) ? [] : [[null, normalizedA, normalizedB]];
    }

    // Primitives or mixed types
    return equalish(normalizedA, normalizedB) ? [] : [[null, normalizedA, normalizedB]];
};

const getFieldLabel = (key, value) => {
    // Try to get description from enhanced value structure
    if (value && typeof value === 'object' && value.description) {
        return value.description;
    }
    // Fallback to formatting the key
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const logDiff = item => {
    // on create try to show the newly created id
    if (item.log_type.startsWith('CREATE_') || item.log_type === 'BOOKINGITEM_UPDATE') {
        let whatToParse = item.log_data;
        if (item.log_data.new) {
            whatToParse = item.log_data.new;
        }
        return (
            <div style={{ marginBottom: 4, fontSize: '0.6rem' }}>
                {item.log_data.id && item.log_data.id.value && item.log_data.id.value.value && (
                    <span style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                        Created record with ID #{item.log_data.id.value.value}
                    </span>
                )}
                {Object.entries(whatToParse).map(([key, value], idx) => {
                    return (
                        <div key={idx}>
                            {getFieldLabel(key, value)}:{' '}
                            <span
                                style={{
                                    color: '#388e3c',
                                    backgroundColor: '#e8f5e8',
                                    padding: '2px 4px',
                                    borderRadius: 3
                                }}
                            >
                                {show(value)}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }

    const data = item.log_diff;
    if (!data) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
    }

    const top = deepParse(data);
    if (!top || typeof top !== 'object') {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>Invalid diff data</span>;
    }

    const rows = Object.entries(top)
        .map(([key, value]) => {
            const isPair = value && typeof value === 'object' && ('old' in value || 'new' in value);
            const label = getFieldLabel(key, value);

            const oldVal = isPair ? value.old : undefined;
            const newVal = isPair ? value.new : value;

            const changes = objectDiff(oldVal, newVal);
            if (changes.length === 0) {
                return null;
            }

            if (changes.length === 1 && changes[0][0] === null) {
                return (
                    <div key={key} style={{ marginBottom: 4 }}>
                        <strong style={{ color: '#333' }}>{label}:</strong>{' '}
                        {oldVal !== undefined && (
                            <>
                                <span
                                    style={{
                                        color: '#d32f2f',
                                        backgroundColor: '#ffebee',
                                        padding: '2px 4px',
                                        borderRadius: 3
                                    }}
                                >
                                    {show(oldVal)}
                                </span>
                                {' → '}
                            </>
                        )}
                        <span
                            style={{
                                color: '#388e3c',
                                backgroundColor: '#e8f5e8',
                                padding: '2px 4px',
                                borderRadius: 3
                            }}
                        >
                            {show(newVal)}
                        </span>
                    </div>
                );
            }

            return (
                <div key={key} style={{ marginBottom: 4 }}>
                    <strong style={{ color: '#333' }}>{label}:</strong>{' '}
                    {changes.map(([sub, o, n], idx) => (
                        <span key={sub ?? idx}>
                            {sub && <em>{sub}: </em>}
                            <span
                                style={{
                                    color: '#d32f2f',
                                    backgroundColor: '#ffebee',
                                    padding: '2px 4px',
                                    borderRadius: 3
                                }}
                            >
                                {show(o)}
                            </span>
                            {' → '}
                            <span
                                style={{
                                    color: '#388e3c',
                                    backgroundColor: '#e8f5e8',
                                    padding: '2px 4px',
                                    borderRadius: 3
                                }}
                            >
                                {show(n)}
                            </span>
                            {idx < changes.length - 1 ? '; ' : null}
                        </span>
                    ))}
                </div>
            );
        })
        .filter(Boolean);

    return rows.length ? (
        <div style={{ fontSize: '0.875rem', lineHeight: 1.4 }}>{rows}</div>
    ) : (
        <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
    );
};

const AuditlogTable = ({
    data,
    page,
    setPage,
    pageSize,
    setPageSize,
    total = Infinity,
    hideFullLog,
    hideLogDiff
}) => {
    const classes = useStyles();
    const [openRowIds, setOpenRowIds] = useState({});

    const toggleRow = id => {
        setOpenRowIds(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const getLogTypeStyle = logType => {
        const type = logType.toLowerCase();
        if (type.includes('create')) {
            return classes.createAction;
        }
        if (type.includes('update')) {
            return classes.updateAction;
        }
        if (type.includes('delete')) {
            return classes.deleteAction;
        }
        if (type.includes('login') || type.includes('register')) {
            return classes.loginAction;
        }
        return classes.defaultAction;
    };

    const getLogTypeTooltip = logType => {
        const tooltips = {
            CREATE: 'New record created',
            UPDATE: 'Record modified',
            DELETE: 'Record deleted',
            LOGIN: 'User logged in',
            REGISTER: 'New user registered',
            DUPLICATE: 'Record duplicated',
            PASSWORD_RESET: 'Password reset requested'
        };

        const upperType = logType.toUpperCase();
        for (const [key, tooltip] of Object.entries(tooltips)) {
            if (upperType.includes(key)) {
                return tooltip;
            }
        }
        return 'System action performed';
    };

    const formatRelativeTime = dateString => {
        const now = moment();
        const logTime = moment(dateString);
        const diffHours = now.diff(logTime, 'hours');

        if (diffHours < 1) {
            return 'Just now';
        }
        if (diffHours < 24) {
            return `${diffHours}h ago`;
        }
        if (diffHours < 48) {
            return 'Yesterday';
        }
        return logTime.format('MMM D');
    };
    return (
        <Grid container item>
            <TableContainer>
                <Table className="mt-2" style={{ backgroundColor: 'white' }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Log type</TableCell>
                            <TableCell>Date/time</TableCell>
                            <TableCell>Client</TableCell>
                            <TableCell>User</TableCell>
                            {!hideFullLog && <TableCell>Full log</TableCell>}
                            {!hideLogDiff && <TableCell></TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.items?.map(item => {
                            const isOpen = openRowIds[item.id];
                            return (
                                <React.Fragment key={item.id}>
                                    <TableRow hover>
                                        <TableCell>
                                            <Tooltip title={getLogTypeTooltip(item.log_type)}>
                                                <Chip
                                                    label={item.log_type.toUpperCase()}
                                                    size="small"
                                                    className={getLogTypeStyle(item.log_type)}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell className={classes.dateCell}>
                                            <Tooltip
                                                title={moment(item.created_at).format(
                                                    'YYYY-MM-DD HH:mm:ss'
                                                )}
                                            >
                                                <div>
                                                    <div>{formatRelativeTime(item.created_at)}</div>
                                                    <div
                                                        style={{
                                                            fontSize: '0.75rem',
                                                            color: '#666'
                                                        }}
                                                    >
                                                        {moment(item.created_at).format('HH:mm')}
                                                    </div>
                                                </div>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{item.company_name || '-'}</TableCell>
                                        <TableCell>{item.user_name || '-'}</TableCell>
                                        {!hideFullLog && (
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleRow(item.id)}
                                                    className={`${classes.expandIcon} ${
                                                        isOpen ? classes.expandIconOpen : ''
                                                    }`}
                                                >
                                                    <ChevronDownIcon />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                        {!hideLogDiff && <TableCell>{logDiff(item)}</TableCell>}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell
                                            colSpan={8}
                                            style={{ paddingBottom: 0, paddingTop: 0 }}
                                        >
                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                <Box margin={1}>
                                                    <strong>Full Log Data:</strong>
                                                    <RenderObjectTree data={item.log_data} />
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Grid container justifyContent="flex-end">
                <TablePager
                    total={total}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                />
            </Grid>
        </Grid>
    );
};

export default AuditlogTable;
